import React from "react";
import { ArrowRight, MapPin, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

export function Hero() {
  const { t } = useLanguage();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative hero-radial pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-sand bg-white/70 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-aegean">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-terracotta" />
            {t("hero.eyebrow")}
          </div>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink">
            {t("hero.title_line1")}
            <br />
            <span className="text-brand-terracotta">{t("hero.title_line2")}</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-brand-ink/75 leading-relaxed">
            {t("hero.description")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              asChild
              data-testid="hero-primary-cta"
              className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white h-12 px-6 text-base shadow-md shadow-brand-terracotta/20"
            >
              <a href="#contact" className="inline-flex items-center gap-2">
                {t("hero.primary_cta")}
                <ArrowRight size={18} />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-secondary-cta"
              className="rounded-full border-brand-aegean text-brand-aegean hover:bg-brand-aegean hover:text-white h-12 px-6 text-base"
            >
              <a href="#services">{t("hero.secondary_cta")}</a>
            </Button>
          </div>

          <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm">
            {["badge_1", "badge_2", "badge_3"].map((k) => (
              <li key={k} className="flex items-center gap-2 text-brand-ink/80">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-aegean" />
                {t(`hero.${k}`)}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-md lg:max-w-none">
            {/* Arched image frame */}
            <div className="relative overflow-hidden arch-top rounded-b-2xl shadow-xl shadow-brand-aegean/10 border border-brand-sand">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_handwerk-torrevieja/artifacts/1w6y32ag_WhatsApp%20Image%202026-07-07%20at%2012.49.05.webp"
                alt="Christian Reuter an der Costa Blanca"
                className="w-full h-[420px] md:h-[560px] object-cover object-center"
              />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-aegean/50 to-transparent" />
            </div>

            {/* Floating trust card */}
            <div
              data-testid="hero-trust-card"
              className="absolute -left-4 md:-left-8 bottom-6 md:bottom-10 bg-white/95 backdrop-blur-md rounded-2xl border border-brand-sand shadow-lg p-4 pr-5 flex items-center gap-3 animate-float-slow"
            >
              <div className="flex -space-x-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} className="fill-brand-terracotta text-brand-terracotta" />
                ))}
              </div>
              <div className="text-sm">
                <div className="font-semibold text-brand-ink">5.0</div>
                <div className="text-brand-olive text-xs">Torrevieja · Orihuela Costa · Guardamar</div>
              </div>
            </div>

            {/* Location tag */}
            <div className="absolute -right-2 top-8 md:top-10 md:-right-4 bg-brand-aegean text-white rounded-full pl-3 pr-4 py-2 flex items-center gap-2 shadow-lg">
              <MapPin size={14} />
              <span className="text-xs font-semibold tracking-wide">Costa Blanca · ES</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
